'use client';

import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '@/lib/trpc/provider';
import { writeProgramCookie } from '@/lib/program/program-cookie';

/**
 * Program switcher (Spec 2 Decision 4 / AC3 / AC4).
 *
 * Renders NOTHING when `canSwitch` is false — an anonymous visitor on a
 * single-program install, and a logged-in family with exactly one program, never
 * see it. A parent sees it only when an admin grant gives them a second program,
 * and it lists only their granted set.
 *
 * ⚠️ Selecting a program must BOTH write the cookie and bust the client cache.
 * TanStack Query keys on the input object, and the cookie is invisible to that
 * key — so without the invalidate the browser would keep showing the previous
 * program's cached list. `router.refresh()` re-runs the SSR catalog;
 * `onProgramChange` lets the client catalog push the slug into its query key,
 * which is why `programSlug` exists on `catalogFilterInput` at all.
 */
export function ProgramSwitcher({
  onProgramChange,
  className,
}: {
  onProgramChange?: (slug: string) => void;
  className?: string;
}) {
  const t = useTranslations('public.catalog.programSwitcher');
  const trpc = useTRPC();
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data } = useQuery(trpc.storefront.getProgramScope.queryOptions());

  if (!data || !data.canSwitch) return null;

  function handleSelect(slug: string) {
    if (slug === data?.activeSlug) return;
    writeProgramCookie(slug);
    onProgramChange?.(slug);
    void queryClient.invalidateQueries({
      queryKey: trpc.storefront.getCatalog.pathKey(),
    });
    void queryClient.invalidateQueries({
      queryKey: trpc.storefront.getStartingSoonSections.pathKey(),
    });
    void queryClient.invalidateQueries({
      queryKey: trpc.storefront.getProgramScope.pathKey(),
    });
    router.refresh();
  }

  return (
    <div
      className={`flex items-center gap-2 ${className ?? ''}`}
      data-testid="program-switcher"
    >
      <span className="text-sm text-[#6b7280]">{t('label')}:</span>
      <div
        role="radiogroup"
        aria-label={t('ariaLabel')}
        className="inline-flex rounded-lg border border-[#e5e7eb] bg-white p-0.5"
      >
        {data.options.map((option) => {
          const active = option.slug === data.activeSlug;
          return (
            <button
              key={option.slug}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => handleSelect(option.slug)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                active
                  ? 'bg-[#1e306e] text-white'
                  : 'bg-transparent text-[#6b7280] hover:text-[#1e306e]'
              }`}
            >
              {/* Program names come from the DB, not the message files. */}
              {option.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
